import React, { useEffect, useState, useRef } from "react";
import { api, friendlyError } from "../lib/api";
import { PageHeader, Disclaimer } from "../components/AppLayout";
import { ImpactBadge, Pill } from "../lib/ui";
import { Upload, FileText, Scan, Trash2, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const ASSESS_COLOR = {
  "Conforme": "#166534", "Parzialmente conforme": "#92400E", "Potenziale gap": "#9A3412",
  "Non conforme": "#991B1B", "Non applicabile": "#64748B", "Non determinabile": "#475569",
};
const STANDARDS = [
  "EU GMP Annex 1 sterile aseptic", "FDA aseptic processing sterile", "ICH Q9 quality risk management",
  "FDA data integrity cGMP", "ICH Q12 established conditions post-approval",
];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [standard, setStandard] = useState(STANDARDS[0]);
  const [analyzing, setAnalyzing] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => toast.error("Impossibile caricare i documenti"));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "SOP");
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato ed elaborato");
      load();
    } catch (e) { toast.error(friendlyError(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const runGap = async (doc) => {
    setAnalyzing(doc.id); setResult(null);
    try {
      const { data } = await api.post(`/documents/${doc.id}/gap-analysis`, { standard });
      setResult(data);
    } catch (e) { toast.error(friendlyError(e)); } finally { setAnalyzing(null); }
  };

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); load(); } catch (e) { toast.error(friendlyError(e)); }
  };

  const review = async (decision) => {
    if (!result?.analysis_id) return;
    try { await api.post(`/analyses/${result.analysis_id}/review`, { decision }); toast.success(`Valutazione: ${decision}`); }
    catch (e) { toast.error(friendlyError(e)); }
  };

  return (
    <div className="ri-fade-in">
      <PageHeader overline="Document Intelligence" title="Document Intelligence & Gap Analysis"
        subtitle="Carica SOP, VMP, CCS, Quality Agreement. Analizza rispetto a un requisito regolatorio e ottieni una matrice di conformità tracciabile." />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Upload + list */}
        <div className="space-y-4">
          <div onClick={() => fileRef.current?.click()} data-testid="doc-upload-zone"
            className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors">
            <input ref={fileRef} type="file" className="hidden" accept=".pdf,.docx,.xlsx,.pptx,.txt" onChange={upload} data-testid="doc-file-input" />
            <Upload size={22} className="text-blue-600 mx-auto mb-2" />
            <div className="text-sm font-medium">{uploading ? "Caricamento..." : "Carica documento"}</div>
            <div className="text-xs text-slate-400 mt-1">PDF, DOCX, XLSX, PPTX, TXT · max 25MB</div>
          </div>

          <div className="rounded-xl bg-white border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold font-head">Documenti aziendali</div>
            <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
              {docs.length === 0 && <div className="p-4 text-xs text-slate-400">Nessun documento caricato.</div>}
              {docs.map((d) => (
                <div key={d.id} data-testid={`doc-item-${d.id}`} className="px-4 py-3 flex items-center gap-2 group">
                  <FileText size={16} className="text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{d.name}</div>
                    <div className="text-[0.65rem] text-slate-400 font-mono">{d.doc_type} · {(d.size / 1024).toFixed(0)}KB · v{d.version}</div>
                  </div>
                  <button onClick={() => runGap(d)} disabled={analyzing === d.id} data-testid={`doc-analyze-${d.id}`}
                    className="text-xs px-2 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1 disabled:opacity-60">
                    <Scan size={12} /> {analyzing === d.id ? "..." : "Gap"}
                  </button>
                  <button onClick={() => del(d.id)} className="text-slate-300 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-white border border-slate-200 p-4">
            <div className="ri-overline mb-2">Standard di confronto</div>
            <select value={standard} onChange={(e) => setStandard(e.target.value)} data-testid="gap-standard-select"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
              {STANDARDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-2">
          {!result && !analyzing && (
            <div className="rounded-xl border border-dashed border-slate-200 h-full flex items-center justify-center text-center p-10 text-slate-400 text-sm">
              Carica un documento e premi "Gap" per generare la matrice di conformità.
            </div>
          )}
          {analyzing && (
            <div className="rounded-xl bg-white border border-slate-200 p-10 text-center text-slate-400">
              <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
              Analisi del documento rispetto a "{standard}"...
            </div>
          )}
          {result && (
            <div className="space-y-4">
              <div className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="ri-overline mb-1">Executive summary · {result.doc_name}</div>
                <p className="text-sm text-slate-600">{result.executive_summary || result.error}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => review("Accettato")} data-testid="gap-review-accept" className="text-xs px-3 py-1.5 rounded-md bg-green-600 text-white flex items-center gap-1"><Check size={13} /> Accetta</button>
                  <button onClick={() => review("Modificato")} className="text-xs px-3 py-1.5 rounded-md bg-slate-200 text-slate-700">Modifica</button>
                  <button onClick={() => review("Rifiutato")} className="text-xs px-3 py-1.5 rounded-md bg-red-100 text-red-700 flex items-center gap-1"><X size={13} /> Rifiuta</button>
                  <button onClick={() => review("Ulteriore approfondimento")} className="text-xs px-3 py-1.5 rounded-md bg-slate-200 text-slate-700">Approfondisci</button>
                </div>
              </div>

              {result.findings?.length > 0 && (
                <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
                  <table className="ri-table">
                    <thead><tr><th>Requisito</th><th>Fonte</th><th>Evidenza nel doc</th><th>Valutazione</th><th>Gap</th><th>Rischio</th><th>Azione</th></tr></thead>
                    <tbody>
                      {result.findings.map((f, i) => (
                        <tr key={i} data-testid={`gap-finding-${i}`}>
                          <td className="text-xs">{f.requirement}</td>
                          <td className="text-xs text-slate-400">{f.source}</td>
                          <td className="text-xs">{f.evidence_in_document}</td>
                          <td><span className="text-xs font-medium" style={{ color: ASSESS_COLOR[f.assessment] || "#475569" }}>{f.assessment}</span></td>
                          <td className="text-xs">{f.gap}</td>
                          <td>{f.risk && <ImpactBadge level={f.risk} small />}</td>
                          <td className="text-xs">{f.recommended_action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {result.sources?.length > 0 && (
                <div className="text-xs text-slate-500">
                  <span className="ri-overline">Fonti regolatorie</span>
                  <div className="mt-1 space-y-1">
                    {result.sources.map((s) => <div key={s.n}>[{s.n}] {s.authority} — {s.title} {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-600"><ExternalLink size={11} className="inline" /></a>}</div>)}
                  </div>
                </div>
              )}
              {result.disclaimer && <Disclaimer text={result.disclaimer} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
